import { FormatPill } from '../components/simulation/FormatPill';
import { ParentCornerPicker } from '../components/simulation/ParentCornerPicker';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FormattedNumberInput } from '../components/ui/formatted-number-input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ArrowRight, 
  Building2, 
  Users, 
  Wallet, 
  Settings2,
  FileText,
  Calculator,
  Loader2,
  Check,
  MapPin,
  Search,
  Camera,
  X,
  Upload,
  Image,
  ExternalLink,
  Trash2,
  Save,
  Info,
  AlertTriangle,
  Flame
} from 'lucide-react';
import { TemperatureStep } from '../components/simulation/TemperatureStep';

const STEPS = [
  { id: 1, icon: Building2, key: 'generalInfo' },
  { id: 2, icon: Users, key: 'competitionInfo' },
  { id: 3, icon: Wallet, key: 'financialData' },
  { id: 4, icon: Settings2, key: 'capexInvestment' },
  { id: 5, icon: Calculator, key: 'hypothesisParams' },
  { id: 6, icon: Flame, key: 'dealTemperature' },
  // Photo step removed - photos can be added after simulation creation
];

// Date du jour + 10 semaines, ramenée au jeudi le plus proche (format YYYY-MM-DD local).
const defaultOpeningDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 70);
  let diff = 4 - d.getDay();
  if (diff > 3) diff -= 7;
  if (diff < -3) diff += 7;
  d.setDate(d.getDate() + diff);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const isThursday = (s) => {
  if (!s) return true;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).getDay() === 4;
};

const plusDays = (base, n) => { const d = new Date(base); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const freshFormData = () => ({ ...defaultFormData, general_info: { ...defaultFormData.general_info, opening_date: defaultOpeningDate(), estimated_signature_date: plusDays(new Date(), 45) } });

const defaultFormData = {
  deal_temperature: { level: '', comment: '' },
  general_info: {
    project_name: '',
    project_type: 'Nouvelle ouverture',
    format: 'corner',
    opening_date: '',
    retailer_name: '',
    address: '',
    postal_code: '',
    city: '',
    store_surface_m2: null,
    zone_rm: '',
    zone_avg_age: null,
    zone_csp: '',
    revenu_median: null,
    renouvellement_change_inox: '',
    renouvellement_change_vitrines: '',
    renouvellement_change_plv: '',
  },
  competition_info: {
    has_competitor: false,
    competitor_name: '',
    engagement_duration: null,
    current_discount: null,
    current_concept: '',
    engagement_end_date: '',
    exit_reasons: '',
    competitor_annual_ca: null,
    competitor_monthly_ca: null,
    competitor_commission: null,
    has_other_restaurant: false,
    has_pan_asian: false,
  },
  capex_investment: {
    corner_investor: 'CCD',
    vitrine_investor: 'CCD',
    inox_investor: 'CCD',
    concept: 'WASABI, MME WANG',
    corner_location: '',
    location_rating: '',
    corner_project: '',
    corner_dismantling: false,
    dismantling_by: '',
    cooling_cell_provider: 'CLIENT',
    cooling_cell_on_corner: true,
    wok_provider: 'CLIENT',
    wok_on_corner: true,
    capex_corner: 12000,
    capex_vitrine: 8000,  // Default: 12000 + 8000 + 7000 = 27000€ (Corner+Vitrine+Inox)
    capex_inox: 7000,
    capex_computer_cash: 1000,  // Always CCD
    capex_local_legal: 0,
    capex_robots: 0,
    vitrine_composition: { v250: 0, v196: 0, v146: 0, v116: 0, bac120: 0 },
    labo_to_equip: false,
    labo_inox_investor: 'CCD',
    labo_comment: '',
    capex_inox_labo: 0,
    rice_machine: false,
    capex_rice_machine: 0,
  },
  financial_data: {
    annual_ca_hors_carburant: null,
    annual_ca_alimentaire: null,
    annual_ca_poissonnerie: null,
    annual_frequentation: null,
    global_ticket_moyen: null,
  },
  hypothesis_params: {
    sushi_captation_rate: 0.03,
    wok_captation_rate: 0.01,
    sushi_basket_avg: 13,
    wok_basket_avg: 7.5,
    alimentaire_captation_rate: 0.01,
    poissonnerie_captation_rate: 0.20,
  },
  contract_info: {
    commission_model: 'Variable commission model',
    flat_commission_rate: null,
    negotiated_commission_rate: 0.15,
    contract_duration_months: 36,
    ca_previsionnel_ht: null,
    tva_rate: 0.10,
  },
  sales_evolution: {
    price_increase_y2: 0,
    price_increase_y3: 0,
    price_increase_y4: 0,
    lfl_increase_y2: 0.01,
    lfl_increase_y3: 0.01,
    lfl_increase_y4: 0.01,
  },
  labor_costs: {
    manager_multisite_count: 0.1,
    manager_multisite_hours: 40,
    manager_multisite_rate: -17.51,
    chef_confirme_count: 0,
    chef_confirme_hours: 40,
    chef_confirme_rate: -17.51,
    chef_junior_count: 1,
    chef_junior_hours: 40,
    chef_junior_rate: -15.56,
    second_count: 1,
    second_hours: 40,
    second_rate: -13.68,
    commis_count: 1,
    commis_hours: 37.5,
    commis_rate: -12.72,
    support_ops_cost: -15516,
  },
};

const FormInput = ({ label, id, type = 'text', value, onChange, placeholder, required, className, inputMode, step, disabled = false }) => (
  <div className={className}>
    <Label htmlFor={id} className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5 block">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <Input
      id={id}
      type={type}
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder}
      className="h-12 bg-slate-50 dark:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed"
      inputMode={inputMode}
      step={step}
      disabled={disabled}
      data-testid={`input-${id}`}
    />
  </div>
);

// Money input with thousands separator (spaces)
const FormMoneyInput = ({ label, id, value, onChange, placeholder, required, className, suffix = '€', allowNegative = false, highlightError = false, disabled = false, hint, compact = false }) => (
  <div className={className}>
    <Label htmlFor={id} className={`font-medium text-slate-600 dark:text-slate-300 block ${compact ? 'text-xs mb-1' : 'text-sm mb-1.5'}`}>
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <FormattedNumberInput
      id={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={`bg-slate-50 dark:bg-slate-800 ${compact ? 'h-9 text-sm' : 'h-12'} ${disabled ? 'opacity-70 cursor-not-allowed bg-slate-100 dark:bg-slate-800/60' : ''} ${highlightError ? 'ring-2 ring-red-400 border-red-400 animate-pulse' : ''}`}
      suffix={suffix}
      allowNegative={allowNegative}
      data-testid={`input-${id}`}
    />
    {hint && <p className={`text-slate-400 mt-1 ${compact ? 'text-[10px] leading-tight' : 'text-[11px]'}`}>{hint}</p>}
  </div>
);
const FormMoneyInputOld = ({ label, id, value, onChange, placeholder, required, className, suffix = '€', allowNegative = false, highlightError = false }) => (
  <div className={className}>
    <Label htmlFor={id} className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5 block">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <FormattedNumberInput
      id={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`h-12 bg-slate-50 dark:bg-slate-800 ${highlightError ? 'ring-2 ring-red-400 border-red-400 animate-pulse' : ''}`}
      suffix={suffix}
      allowNegative={allowNegative}
      data-testid={`input-${id}`}
    />
  </div>
);

const FormSelect = ({ label, id, value, onChange, options, required, className, placeholder, highlightError = false, disabled = false }) => {
  // Filter out empty value options since Radix UI Select doesn't support them
  // Use the empty option's label as placeholder instead
  const placeholderOption = options.find(opt => opt.value === '');
  const validOptions = options.filter(opt => opt.value !== '');
  const effectivePlaceholder = placeholder || placeholderOption?.label || '-- Sélectionner --';
  
  return (
    <div className={className}>
      <Label htmlFor={id} className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5 block">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger 
          className={`h-12 bg-slate-50 dark:bg-slate-800 ${disabled ? 'opacity-70 cursor-not-allowed bg-slate-100 dark:bg-slate-800/60' : ''} ${highlightError ? 'ring-2 ring-red-400 border-red-400 animate-pulse' : ''}`}
          data-testid={`select-${id}`}
          onPointerDown={(e) => {
            // Prevent default touch behavior that might interfere on mobile
            e.stopPropagation();
          }}
        >
          <SelectValue placeholder={effectivePlaceholder} />
        </SelectTrigger>
        <SelectContent 
          position="popper" 
          sideOffset={4}
          className="max-h-[300px] overflow-y-auto z-[9999]"
        >
          {validOptions.map((opt) => (
            <SelectItem 
              key={opt.value} 
              value={opt.value}
              onTouchEnd={(e) => {
                // Ensure touch selection works on mobile
                e.stopPropagation();
              }}
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const FormSwitch = ({ label, id, checked, onChange, className }) => (
  <div className={`flex items-center justify-between ${className}`}>
    <Label htmlFor={id} className="text-sm font-medium text-slate-600 dark:text-slate-300">
      {label}
    </Label>
    <Switch id={id} checked={checked} onCheckedChange={onChange} data-testid={`switch-${id}`} />
  </div>
);

const FormCheckboxGroup = ({ label, id, value, onChange, options, className }) => {
  const selectedValues = value ? value.split(', ').filter(v => v) : [];
  
  const handleToggle = (optionValue) => {
    let newValues;
    if (selectedValues.includes(optionValue)) {
      newValues = selectedValues.filter(v => v !== optionValue);
    } else {
      newValues = [...selectedValues, optionValue];
    }
    onChange(newValues.join(', '));
  };
  
  return (
    <div className={className}>
      <Label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-3 block">
        {label}
      </Label>
      <div className="flex flex-wrap gap-4">
        {options.map((opt) => (
          <div key={opt.value} className="flex items-center space-x-2">
            <Checkbox
              id={`${id}-${opt.value}`}
              checked={selectedValues.includes(opt.value)}
              onCheckedChange={() => handleToggle(opt.value)}
              data-testid={`checkbox-${id}-${opt.value}`}
            />
            <Label 
              htmlFor={`${id}-${opt.value}`}
              className="text-sm font-medium cursor-pointer text-slate-700 dark:text-slate-300"
            >
              {opt.label}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== Composition des vitrines ====================
const VITRINE_TYPES = [
  { key: 'v250', label: 'Vitrine 250 cm', price: 8000 },
  { key: 'v196', label: 'Vitrine 196 cm', price: 6000 },
  { key: 'v146', label: 'Vitrine 146 cm', price: 4000 },
  { key: 'v116', label: 'Vitrine 116 cm', price: 3300 },
  { key: 'bac120', label: 'Bac réfrigéré 120 x 120', price: 4000 },
];

const computeVitrineTotal = (composition) => {
  if (!composition) return 0;
  return VITRINE_TYPES.reduce((sum, v) => sum + (Number(composition[v.key]) || 0) * v.price, 0);
};

const VitrineCompositionSection = ({ composition, onChange, language }) => {
  const comp = composition || {};
  const fmt = (n) => `${(n || 0).toLocaleString('fr-FR')} €`;

  const setQty = (key, qty) => {
    onChange({ ...comp, [key]: qty });
  };

  const total = computeVitrineTotal(comp);

  return (
    <div className="pt-4 border-t border-slate-200 dark:border-slate-700" data-testid="vitrine-composition">
      <div className="mb-3">
        <h4 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold">?</span>
          {language === 'fr' ? 'Comment se composent les vitrines de votre corner ?' : 'How is your corner composed?'}
        </h4>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {language === 'fr'
            ? 'Cochez chaque élément présent et indiquez la quantité (1 ou 2). Le montant CAPEX Vitrine est calculé automatiquement.'
            : 'Check each item and set the quantity (1 or 2). The CAPEX Vitrine amount is computed automatically.'}
        </p>
      </div>

      <div className="space-y-2">
        {VITRINE_TYPES.map((v) => {
          const qty = Number(comp[v.key]) || 0;
          const active = qty > 0;
          return (
            <div
              key={v.key}
              className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors ${active ? 'border-indigo-300 bg-indigo-50/60 dark:bg-indigo-900/20' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}
              data-testid={`vitrine-row-${v.key}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Checkbox
                  id={`vit-${v.key}`}
                  checked={active}
                  onCheckedChange={(c) => setQty(v.key, c ? 1 : 0)}
                  data-testid={`vitrine-check-${v.key}`}
                />
                <Label htmlFor={`vit-${v.key}`} className="cursor-pointer">
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{v.label}</span>
                  <span className="text-xs text-slate-400 ml-2">{fmt(v.price)} / u.</span>
                </Label>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                {active && (
                  <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                    {[1, 2].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setQty(v.key, n)}
                        className={`px-3 py-1 text-sm font-semibold transition-colors ${qty === n ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                        data-testid={`vitrine-qty-${v.key}-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                )}
                <span className={`text-sm font-bold tabular-nums w-20 text-right ${active ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-300'}`}>
                  {active ? fmt(qty * v.price) : '—'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-3 p-3 rounded-lg bg-indigo-600 text-white" data-testid="vitrine-total">
        <span className="text-sm font-semibold">{language === 'fr' ? 'Total CAPEX Vitrine' : 'Total CAPEX Vitrine'}</span>
        <span className="text-lg font-bold tabular-nums">{fmt(total)}</span>
      </div>
    </div>
  );
};

// Address Autocomplete Component using Google Places API
const AddressAutocomplete = ({ onAddressSelect, language }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const debounceRef = useRef(null);

  // Initialize Google Places services
  useEffect(() => {
    const initGooglePlaces = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        // Create a dummy div for PlacesService (required)
        const dummyDiv = document.createElement('div');
        placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
        return true;
      }
      return false;
    };

    // Try to init immediately
    if (!initGooglePlaces()) {
      // If not loaded yet, wait and retry
      const checkInterval = setInterval(() => {
        if (initGooglePlaces()) {
          clearInterval(checkInterval);
        }
      }, 100);
      
      // Clear after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);
    }
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddress = useCallback(async (searchQuery) => {
    if (searchQuery.length < 3 || !autocompleteService.current) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    
    try {
      autocompleteService.current.getPlacePredictions(
        {
          input: searchQuery,
          componentRestrictions: { country: 'fr' },
          types: ['establishment', 'geocode'],
          language: 'fr'
        },
        (predictions, status) => {
          setIsLoading(false);
          
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.map(prediction => ({
              place_id: prediction.place_id,
              display_name: prediction.description,
              main_text: prediction.structured_formatting?.main_text || '',
              secondary_text: prediction.structured_formatting?.secondary_text || '',
              types: prediction.types || []
            })));
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
          }
        }
      );
    } catch (error) {
      console.error('Google Places search error:', error);
      setIsLoading(false);
      setSuggestions([]);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // Debounce the search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchAddress(value);
    }, 300);
  };

  const handleSelect = (suggestion) => {
    setQuery(suggestion.display_name);
    setShowSuggestions(false);
    setIsLoading(true);
    
    // Get place details to extract address components
    if (placesService.current && suggestion.place_id) {
      placesService.current.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ['address_components', 'formatted_address', 'geometry', 'name']
        },
        (place, status) => {
          setIsLoading(false);
          
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            // Extract address components
            let streetNumber = '';
            let route = '';
            let postalCode = '';
            let city = '';
            let department = '';
            
            place.address_components?.forEach(component => {
              const types = component.types;
              if (types.includes('street_number')) {
                streetNumber = component.long_name;
              }
              if (types.includes('route')) {
                route = component.long_name;
              }
              if (types.includes('postal_code')) {
                postalCode = component.long_name;
              }
              if (types.includes('locality')) {
                city = component.long_name;
              }
              if (types.includes('administrative_area_level_2')) {
                // Extract department number from postal code if available
                department = postalCode ? postalCode.substring(0, 2) : '';
              }
            });
            
            // If department not found, extract from postal code
            if (!department && postalCode) {
              department = postalCode.substring(0, 2);
            }
            
            const fullAddress = streetNumber && route 
              ? `${streetNumber} ${route}` 
              : place.formatted_address || suggestion.display_name;
            
            onAddressSelect({
              address: place.formatted_address || suggestion.display_name,
              postal_code: postalCode,
              city: city,
              department: department
            });
          } else {
            // Fallback if details fail
            onAddressSelect({
              address: suggestion.display_name,
              postal_code: '',
              city: '',
              department: ''
            });
          }
        }
      );
    } else {
      setIsLoading(false);
      onAddressSelect({
        address: suggestion.display_name,
        postal_code: '',
        city: '',
        department: ''
      });
    }
  };

  // Get icon based on place type
  const getPlaceIcon = (types) => {
    if (types.includes('supermarket') || types.includes('grocery_or_supermarket')) return '🛒';
    if (types.includes('store') || types.includes('shopping_mall')) return '🏪';
    if (types.includes('restaurant') || types.includes('food')) return '🍽️';
    if (types.includes('street_address') || types.includes('route')) return '🛣️';
    if (types.includes('locality') || types.includes('administrative_area_level_1')) return '🏛️';
    if (types.includes('establishment')) return '🏢';
    return '📍';
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5 block">
        <MapPin className="w-4 h-4 inline mr-1" />
        {language === 'fr' ? 'Rechercher une adresse' : 'Search address'}
      </Label>
      <div className="relative">
        <Input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder={language === 'fr' ? 'Ex: Leclerc Pian Médoc, Carrefour Paris, Intermarché Lyon...' : 'Ex: Leclerc Pian Médoc, Carrefour Paris...'}
          className="pl-10 h-12"
          data-testid="address-search-input"
        />
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        {isLoading && (
          <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" />
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id || index}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-0"
              data-testid={`address-suggestion-${index}`}
            >
              <div className="flex items-start gap-3">
                <span className="text-lg flex-shrink-0 mt-0.5">
                  {getPlaceIcon(suggestion.types)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    {suggestion.main_text}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    {suggestion.secondary_text}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      
      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
        {language === 'fr' 
          ? 'Recherchez un magasin, une adresse ou une ville (Google Places)' 
          : 'Search for a store, address or city (Google Places)'}
      </p>
    </div>
  );
};

const isKept = (fd, key) => fd.general_info?.project_type === 'Renouvellement contrat' && fd.general_info?.[`renouvellement_change_${key}`] === 'Non';

export default function NewSimulationPage() {
  const { api, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams(); // Get simulation ID if editing
  const isEditMode = !!id;
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [simLocked, setSimLocked] = useState(false);
  const [caPerEmployee, setCaPerEmployee] = useState(120000);
  const laborAutoAppliedRef = useRef(false);
  const projectNameManualRef = useRef(false);
  // La nouvelle mise à jour étape 4 ne s'applique qu'aux simulations non validées/signées/NO GO
  const applyNewCapexRules = !isEditMode || !simLocked;
  const [currentStep, setCurrentStep] = useState(1);
  
  const [formData, setFormData] = useState(freshFormData);
  // Vitrine déportée : étapes 1 à 4 seulement (étape 5 gardée par défaut)
  const isVitrine = formData.general_info?.format === 'vitrine_deportee';
  const activeSteps = isVitrine ? STEPS.slice(0, 4) : STEPS;
  const safeCurrentStep = Math.min(Math.max(currentStep, 1), activeSteps.length);
  const currentStepData = activeSteps[safeCurrentStep - 1];
  const [loading, setLoading] = useState(false);
  const [loadingSimulation, setLoadingSimulation] = useState(false);
  const [highlightedFields, setHighlightedFields] = useState(new Set()); // Field IDs to flash red when user tries Next with missing fields
  const [includedHypotheses, setIncludedHypotheses] = useState({ h1: true, h2: true, h3: true });
  const [enseigneOptions, setEnseigneOptions] = useState([]);

  // Référentiel fermé des enseignes (Phase 2) — administrable
  useEffect(() => {
    api().get('/referentiels/enseignes')
      .then((r) => setEnseigneOptions((r.data?.values || []).map((v) => ({ value: v.value, label: v.value }))))
      .catch(() => {});
  }, [api]);

  // ===== AUTO-SAVE BROUILLON (NEW MODE ONLY) =====
  // Persist the form locally so a session expiration, accidental tab close or
  // navigation never loses the work in progress. Per-user key avoids mixing
  // drafts between users on a shared device. Edit mode is excluded to avoid
  // overwriting an existing simulation with stale draft data.
  const draftKey = useMemo(
    () => (user?.id ? `capex_sim_draft_${user.id}` : null),
    [user?.id]
  );
  const draftRestoredRef = useRef(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftRestoredToast, setDraftRestoredToast] = useState(false);

  // Restore draft on first mount (NEW mode, before any autosave can clobber)
  useEffect(() => {
    if (isEditMode || !draftKey || draftRestoredRef.current) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft?.formData && typeof draft.formData === 'object') {
          setFormData(draft.formData);
          if (typeof draft.currentStep === 'number') setCurrentStep(draft.currentStep);
          if (draft.includedHypotheses) setIncludedHypotheses(draft.includedHypotheses);
          setDraftSavedAt(draft.savedAt || null);
          setDraftRestoredToast(true);
        }
      }
    } catch (e) {
      console.warn('Draft restore failed:', e);
    } finally {
      draftRestoredRef.current = true;
    }
  }, [isEditMode, draftKey]);

  // Show the "draft restored" toast once (separated from restore effect so the
  // language context is available — toast() is called on next tick).
  useEffect(() => {
    if (draftRestoredToast) {
      toast.success(
        language === 'fr'
          ? '✏️ Brouillon restauré depuis votre dernière session'
          : '✏️ Draft restored from your last session',
        { duration: 5000, id: 'draft-restored' }
      );
      setDraftRestoredToast(false);
    }
  }, [draftRestoredToast, language]);

  // Auto-save (debounced 800ms) on any form change, NEW mode only.
  useEffect(() => {
    if (isEditMode || !draftKey || !draftRestoredRef.current) return;
    const handle = setTimeout(() => {
      try {
        const payload = {
          formData,
          currentStep,
          includedHypotheses,
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem(draftKey, JSON.stringify(payload));
        setDraftSavedAt(payload.savedAt);
      } catch (_) {
        /* localStorage may be full or blocked — silently skip */
      }
    }, 800);
    return () => clearTimeout(handle);
  }, [formData, currentStep, includedHypotheses, isEditMode, draftKey]);

  // Manual reset: wipe draft and reset the form to defaults
  const clearDraft = useCallback(() => {
    if (!draftKey) return;
    const ok = window.confirm(
      language === 'fr'
        ? 'Effacer le brouillon et repartir d\'un formulaire vierge ? Cette action est irréversible.'
        : 'Clear the draft and start with a blank form? This cannot be undone.'
    );
    if (!ok) return;
    try { localStorage.removeItem(draftKey); } catch (_) { /* ignore */ }
    setFormData(freshFormData());
    setCurrentStep(1);
    setIncludedHypotheses({ h1: true, h2: true, h3: true });
    setDraftSavedAt(null);
    // The setState calls above will retrigger the autosave effect (debounced 800ms)
    // which would re-write a "default" draft to localStorage. Re-clear after the
    // debounce window so the storage truly stays empty until the user types again.
    setTimeout(() => {
      try { localStorage.removeItem(draftKey); } catch (_) { /* ignore */ }
      setDraftSavedAt(null);
    }, 1000);
    toast.success(language === 'fr' ? 'Brouillon effacé.' : 'Draft cleared.');
  }, [draftKey, language]);

  // Toggle hypothesis inclusion
  const toggleHypothesis = (key) => {
    setIncludedHypotheses(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Cluster configuration (matching backend DEFAULT_CLUSTERS)
  const CLUSTERS = [
    { id: 'cluster_1', name: 'Cluster 1', min_ca: 0, max_ca: 15000, food_cost_rate: 0.2536, other_cost_rate: 0.055 },
    { id: 'cluster_2', name: 'Cluster 2', min_ca: 15000, max_ca: 30000, food_cost_rate: 0.3036, other_cost_rate: 0.0315 },
    { id: 'cluster_3', name: 'Cluster 3', min_ca: 30000, max_ca: 50000, food_cost_rate: 0.2762, other_cost_rate: 0.0267 },
    { id: 'cluster_4', name: 'Cluster 4', min_ca: 50000, max_ca: 75000, food_cost_rate: 0.2883, other_cost_rate: 0.0344 },
    { id: 'cluster_5', name: 'Cluster 5', min_ca: 75000, max_ca: Infinity, food_cost_rate: 0.2806, other_cost_rate: 0.0279 },
  ];

  // Calculate estimated cluster based on financial data and hypothesis
  const getEstimatedCluster = useCallback(() => {
    const { financial_data, hypothesis_params } = formData;
    
    // Calculate estimated annual CA using the 3 hypotheses
    const annualFreq = financial_data.annual_frequentation || 0;
    const caAlimentaire = financial_data.annual_ca_alimentaire || 0;
    const caPoissonnerie = financial_data.annual_ca_poissonnerie || 0;
    
    // Hypothesis 1: Based on frequentation
    const sushiCaptation = hypothesis_params.sushi_captation_rate || 0.03;
    const sushiBasket = hypothesis_params.sushi_basket_avg || 13;
    const wokCaptation = hypothesis_params.wok_captation_rate || 0.01;
    const wokBasket = hypothesis_params.wok_basket_avg || 7.5;
    const sushiClients = annualFreq * sushiCaptation;
    const wokClients = annualFreq * wokCaptation;
    const h1CaHt = (sushiClients * sushiBasket + wokClients * wokBasket) / 1.1;
    
    // Hypothesis 2: Based on CA alimentaire
    const alimentaireCaptation = hypothesis_params.alimentaire_captation_rate || 0.01;
    const h2CaHt = caAlimentaire * alimentaireCaptation / 1.1;
    
    // Hypothesis 3: Based on poissonnerie
    const poissonnerieCaptation = hypothesis_params.poissonnerie_captation_rate || 0.20;
    const h3CaHt = caPoissonnerie * poissonnerieCaptation / 1.1;
    
    // Average of the 3 hypotheses
    const avgCaHt = (h1CaHt + h2CaHt + h3CaHt) / 3;
    const monthlyCa = avgCaHt / 12;
    
    // Find matching cluster
    for (const cluster of CLUSTERS) {
      if (monthlyCa >= cluster.min_ca && monthlyCa < cluster.max_ca) {
        return { ...cluster, monthlyCa, avgCaHt, h1CaHt, h2CaHt, h3CaHt };
      }
    }
    return { ...CLUSTERS[CLUSTERS.length - 1], monthlyCa, avgCaHt, h1CaHt, h2CaHt, h3CaHt };
  }, [formData]);

  // Calculate recommended commission rate based on projected CA
  const calculateRecommendedCommission = useCallback(() => {
    const { financial_data, hypothesis_params, capex_investment } = formData;
    
    // Estimate CA based on hypothesis 1 (frequentation-based) - simplified calculation
    const annualFreq = financial_data.annual_frequentation || 0;
    const sushiCaptation = hypothesis_params.sushi_captation_rate || 0.025;
    const sushiBasket = hypothesis_params.sushi_basket_avg || 13;
    const wokCaptation = hypothesis_params.wok_captation_rate || 0.01;
    const wokBasket = hypothesis_params.wok_basket_avg || 7.5;
    
    // Hypothesis 1: Based on frequentation
    const sushiClients = annualFreq * sushiCaptation;
    const wokClients = annualFreq * wokCaptation;
    const h1CaHt = (sushiClients * sushiBasket + wokClients * wokBasket) / 1.1; // Remove TVA
    
    // Hypothesis 2: Based on CA alimentaire
    const caAlimentaire = financial_data.annual_ca_alimentaire || 0;
    const alimentaireCaptation = hypothesis_params.alimentaire_captation_rate || 0.01;
    const h2CaHt = caAlimentaire * alimentaireCaptation / 1.1;
    
    // Average of hypotheses
    const avgCaHt = (h1CaHt + h2CaHt) / 2;
    
    // Get investor type from capex (use corner_investor as reference)
    const investor = capex_investment.corner_investor || 'CLIENT';
    
    // Commission rate table based on CA and investor
    if (investor === 'CCD') {
      if (avgCaHt <= 350000) return 0.15;
      if (avgCaHt <= 500000) return 0.18;
      if (avgCaHt <= 600000) return 0.19;
      if (avgCaHt <= 700000) return 0.20;
      if (avgCaHt <= 800000) return 0.21;
      if (avgCaHt <= 1000000) return 0.22;
      return 0.23;
    } else {
      if (avgCaHt <= 350000) return 0.18;
      if (avgCaHt <= 500000) return 0.20;
      if (avgCaHt <= 600000) return 0.21;
      if (avgCaHt <= 700000) return 0.22;
      if (avgCaHt <= 800000) return 0.23;
      if (avgCaHt <= 1000000) return 0.24;
      return 0.25;
    }
  }, [formData]);

  // Load existing simulation data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchSimulation = async () => {
        setLoadingSimulation(true);
        try {
          const response = await api().get(`/simulations/${id}`);
          const sim = response.data;
          // Verrou : les simulations déjà validées/signées/NO GO ne reçoivent PAS la nouvelle
          // mise à jour de l'étape 4 (composition vitrines, labo, lecture seule CAPEX).
          const locked = (
            ['approved', 'rejected'].includes(sim.admin_status) ||
            ['signed', 'won', 'lost', 'nogo'].includes(sim.business_status)
          );
          setSimLocked(locked);
          // Map the simulation data to form data structure
          setFormData({
            general_info: {
              ...sim.input_data.general_info,
              estimated_signature_date: sim.input_data.general_info?.estimated_signature_date ?? (sim.created_at ? plusDays(sim.created_at, 45) : ''),
            },
            competition_info: sim.input_data.competition_info,
            capex_investment: sim.input_data.capex_investment,
            financial_data: sim.input_data.financial_data,
            hypothesis_params: sim.input_data.hypothesis_params,
            contract_info: sim.input_data.contract_info,
            sales_evolution: sim.input_data.sales_evolution,
            labor_costs: sim.input_data.labor_costs,
            deal_temperature: sim.input_data.deal_temperature || { level: '', comment: '' },
          });
        } catch (error) {
          console.error('Failed to load simulation:', error);
          toast.error(language === 'fr' ? 'Erreur lors du chargement' : 'Failed to load simulation');
          navigate('/history');
        } finally {
          setLoadingSimulation(false);
        }
      };
      fetchSimulation();
    }
  }, [id, isEditMode, api, navigate, language]);

  const updateFormData = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const updateVitrineComposition = (newComp) => {
    const total = computeVitrineTotal(newComp);
    setFormData((prev) => ({
      ...prev,
      capex_investment: {
        ...prev.capex_investment,
        vitrine_composition: newComp,
        capex_vitrine: isKept(prev, 'vitrines') ? 0 : total,
      },
    }));
  };

  // Renouvellement : « Renouvellement investissement » par catégorie (Non = matériel existant conservé → CAPEX 0 €)
  const RENEWAL_CATS = { corner: ['plv', 'capex_corner', 12000], vitrine: ['vitrines', 'capex_vitrine', 8000], inox: ['inox', 'capex_inox', 7000] };
  const setRenewalInvest = (cat, invest) => {
    const [key, field, def] = RENEWAL_CATS[cat];
    setFormData((prev) => {
      const cx = prev.capex_investment;
      const restored = cat === 'vitrine' ? (computeVitrineTotal(cx.vitrine_composition) || def) : (Number(cx[field]) || def);
      return {
        ...prev,
        general_info: { ...prev.general_info, [`renouvellement_change_${key}`]: invest ? 'Oui' : 'Non' },
        capex_investment: { ...cx, [field]: invest ? restored : 0 },
      };
    });
  };
  const isRenewal = formData.general_info.project_type === 'Renouvellement contrat';
  // Champs SMAPPEN vides signalés en orange (conseillé, non bloquant)
  const smappenEmpty = (k) => { const v = formData.general_info[k]; return v === null || v === undefined || v === '' || v === 0; };
  const smappenMissingCount = ['store_surface_m2', 'zone_avg_age', 'zone_csp', 'revenu_median'].filter(smappenEmpty).length;
  const smappenMiss = (k) => (smappenEmpty(k) ? 'rounded-lg ring-2 ring-orange-400 bg-orange-50/60 p-2 -m-2 transition-shadow' : 'transition-shadow');
  const kept = (cat) => isKept(formData, RENEWAL_CATS[cat][0]);
  const renewalToggle = (cat) => {
    if (!isRenewal) return null;
    const invest = !kept(cat);
    return (
      <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs cursor-pointer transition-colors ${invest ? 'border-orange-300 bg-orange-50/60' : 'border-slate-200 bg-slate-50'}`} data-testid={`renewal-invest-${cat}`}>
        <Checkbox checked={invest} onCheckedChange={(c) => setRenewalInvest(cat, !!c)} data-testid={`renewal-invest-${cat}-check`} />
        <span className="leading-tight">
          <span className="block font-semibold text-slate-700 whitespace-nowrap">Renouvellement investissement</span>
          <span className={`block text-[11px] font-bold ${invest ? 'text-orange-700' : 'text-slate-500'}`}>{invest ? 'Oui — CAPEX compté' : 'Non — matériel conservé, 0 €'}</span>
        </span>
      </label>
    );
  };

  const updateProjectType = (v) => {
    setFormData((prev) => {
      if (v === 'Renouvellement contrat') return { ...prev, general_info: { ...prev.general_info, project_type: v } };
      const cx = { ...prev.capex_investment };
      Object.values(RENEWAL_CATS).forEach(([key, field, def]) => {
        if (prev.general_info[`renouvellement_change_${key}`] === 'Non') cx[field] = field === 'capex_vitrine' ? (computeVitrineTotal(cx.vitrine_composition) || def) : def;
      });
      return { ...prev, capex_investment: cx, general_info: { ...prev.general_info, project_type: v,
        renouvellement_change_inox: '', renouvellement_change_vitrines: '', renouvellement_change_plv: '' } };
    });
  };

  const updateLaboToEquip = (checked) => {
    setFormData((prev) => ({
      ...prev,
      capex_investment: {
        ...prev.capex_investment,
        labo_to_equip: checked,
        capex_inox_labo: checked ? (Number(prev.capex_investment.capex_inox_labo) || 4000) : 0,
      },
    }));
  };

  const updateRiceMachine = (checked) => {
    setFormData((prev) => ({
      ...prev,
      capex_investment: {
        ...prev.capex_investment,
        rice_machine: checked,
        capex_rice_machine: checked ? 9000 : 0,
      },
    }));
  };

  // Fetch admin config (CA moyen par salarié)
  useEffect(() => {
    let cancel = false;
    api().get('/config/labor')
      .then((res) => { if (!cancel && res.data?.ca_per_employee) setCaPerEmployee(res.data.ca_per_employee); })
      .catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Répartition ETP automatique : total = (1 % du CA alimentaire) / CA par salarié (arrondi au plus proche)
  // `ca` reçu correspond déjà à la base (1 % du CA alimentaire).
  const computeStaffing = (ca, perEmp) => {
    const total = perEmp > 0 && ca > 0 ? Math.round(ca / perEmp) : 0;
    return {
      total,
      manager_multisite_count: 0.1,  // Manager multisite : toujours 0,1
      chef_confirme_count: 0,        // Chef confirmé : toujours 0
      chef_junior_count: 1,          // Chef : toujours 1
      second_count: 1,               // Second : toujours 1
      commis_count: Math.max(0, total - 2), // Commis : le reste
    };
  };

  const applyLaborDistribution = (ca) => {
    const s = computeStaffing(ca, caPerEmployee);
    setFormData((prev) => ({
      ...prev,
      labor_costs: {
        ...prev.labor_costs,
        manager_multisite_count: s.manager_multisite_count,
        chef_confirme_count: s.chef_confirme_count,
        chef_junior_count: s.chef_junior_count,
        second_count: s.second_count,
        commis_count: s.commis_count,
      },
    }));
    return s;
  };

  // Auto-application : base ETP = 1 % du CA alimentaire ÷ CA par salarié (arrondi au plus proche).
  // Recalcul à chaque changement du CA alimentaire (nouvelles simulations / sims non verrouillées).
  useEffect(() => {
    if (!applyNewCapexRules) return;
    if (isEditMode && laborAutoAppliedRef.current) return;
    const caAlimentaire = Number(formData.financial_data.annual_ca_alimentaire) || 0;
    const basis = 0.01 * caAlimentaire; // 1 % du CA alimentaire
    if (basis > 0) {
      applyLaborDistribution(basis);
      laborAutoAppliedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.financial_data.annual_ca_alimentaire, caPerEmployee, applyNewCapexRules]);

  // Nom du projet auto = Enseigne + Ville (ex. « Intermarché Bourges »). Stoppé si édité manuellement.
  useEffect(() => {
    if (isEditMode || projectNameManualRef.current) return;
    const enseigne = (formData.general_info.retailer_name || '').trim();
    const ville = (formData.general_info.city || '').trim();
    if (!enseigne || !ville) return;
    const auto = `${enseigne} ${ville}`;
    if (formData.general_info.project_name !== auto) {
      updateFormData('general_info', 'project_name', auto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.general_info.retailer_name, formData.general_info.city]);

  const handleNext = () => {
    // Use shared validator
    const missing = getMissingRequiredFields(safeCurrentStep);
    if (missing.length > 0) {
      toast.error(
        (language === 'fr' ? 'Champs obligatoires manquants : ' : 'Required fields missing: ') + missing.map((m) => m.label).join(', ')
      );
      flashMissingFields(missing);
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, activeSteps.length));
  };

  const editActions = (pos) => isEditMode && (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={() => navigate(`/simulation/${id}`)} disabled={loading} className="gap-2" data-testid={`edit-cancel-btn-${pos}`}>
        <X className="w-4 h-4" />Annuler
      </Button>
      <Button onClick={handleSubmit} disabled={loading} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`edit-save-btn-${pos}`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer et revenir au résultat
      </Button>
    </div>
  );

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Returns the list of missing required fields for a given step (used to disable Next button)
  const getMissingRequiredFields = (step) => {
    const missing = []; // {id, label}
    if (step === 1) {
      const g = formData.general_info;
      if (!g.retailer_name) missing.push({ id: 'retailer_name', label: language === 'fr' ? "Enseigne" : 'Retailer name' });
      if (!g.address) missing.push({ id: 'address', label: language === 'fr' ? 'Adresse' : 'Address' });
      if (!g.postal_code) missing.push({ id: 'postal_code', label: language === 'fr' ? 'Code postal' : 'Postal code' });
      if (!g.city) missing.push({ id: 'city', label: language === 'fr' ? 'Ville' : 'City' });
      if (!g.opening_date) missing.push({ id: 'opening_date', label: language === 'fr' ? "Date prév. d'ouverture" : 'Opening date' });
    }
    if (step === 3) {
      const fd = formData.financial_data;
      if (!fd.annual_ca_alimentaire) missing.push({ id: 'annual_ca_alimentaire', label: language === 'fr' ? 'CA Annuel Alimentaire' : 'Annual Food Revenue' });
      if (!fd.annual_frequentation) missing.push({ id: 'annual_frequentation', label: language === 'fr' ? 'Fréquentation annuelle' : 'Annual Frequentation' });
      if (!fd.global_ticket_moyen) missing.push({ id: 'global_ticket_moyen', label: language === 'fr' ? 'Ticket moyen Global' : 'Global Average Basket' });
    }
    return missing;
  };

  const flashMissingFields = (missing) => {
    const ids = new Set(missing.map((m) => m.id));
    setHighlightedFields(ids);
    // Scroll to first missing
    const first = missing[0];
    if (first) {
      setTimeout(() => {
        const el = document.getElementById(first.id) || document.querySelector(`[data-testid="input-${first.id}"]`) || document.querySelector(`[data-testid="select-${first.id}"]`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 60);
    }
    // Clear after 2.5s
    setTimeout(() => setHighlightedFields(new Set()), 2500);
  };

  const handleSubmit = async () => {
    if (!isEditMode && !isVitrine && !formData.deal_temperature?.level) {
      toast.error('Indiquez la température du dossier (étape 6)');
      setCurrentStep(6);
      return;
    }
    if (isEditMode) {
      const missing = [1, 3].flatMap((st) => getMissingRequiredFields(st));
      if (missing.length > 0) {
        toast.error('Champs obligatoires manquants : ' + missing.map((m) => m.label).join(', '));
        return;
      }
    }
    setLoading(true);
    try {
      let response;
      // Prepare form data without photos (they are uploaded separately)
      const submissionData = { 
        ...formData,
        labor_costs: { ...formData.labor_costs, labor_factor: isVitrine ? 0.1 : 1 },
        ...(isVitrine ? { capex_investment: {
          ...formData.capex_investment,
          capex_corner: 0, capex_inox: 0, capex_computer_cash: 0, capex_local_legal: 0, capex_robots: 0,
          capex_inox_labo: 0, labo_to_equip: false, rice_machine: false, capex_rice_machine: 0,
          corner_dismantling: false, dismantling_by: '',
          capex_vitrine: formData.capex_investment.vitrine_investor === 'CCD' ? 8000 : 0,
        } } : {}),
        included_hypotheses: includedHypotheses // Include hypothesis selection
      };
      delete submissionData.photos;
      
      if (isEditMode) {
        // Update existing simulation
        response = await api().put(`/simulations/${id}`, submissionData);
        toast.success(language === 'fr' ? 'Simulation mise à jour !' : 'Simulation updated!');
      } else {
        // Create new simulation
        response = await api().post('/simulations', submissionData);
        toast.success(t('success'));
      }
      
      const simulationId = response.data.id;
      
      // Upload photos if any
      if (formData.photos && formData.photos.length > 0) {
        try {
          const photoFormData = new FormData();
          formData.photos.forEach((photo) => {
            photoFormData.append('files', photo.file);
          });
          
          await api().post(`/simulations/${simulationId}/photos`, photoFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          toast.success(language === 'fr' 
            ? `${formData.photos.length} photo(s) téléversée(s)`
            : `${formData.photos.length} photo(s) uploaded`);
        } catch (photoError) {
          console.error('Failed to upload photos:', photoError);
          toast.error(language === 'fr' 
            ? 'Erreur lors du téléversement des photos'
            : 'Failed to upload photos');
        }
      }
      // Successful save: wipe the local draft so the user gets a clean form
      // next time they open /simulation/new
      if (!isEditMode && draftKey) {
        try { localStorage.removeItem(draftKey); } catch (_) { /* ignore */ }
      }
      
      navigate(`/simulation/${simulationId}`);
    } catch (error) {
      console.error('Failed to save simulation:', error);
      // The global 401 interceptor handles session expiration. For other errors,
      // surface the most explicit message we can (Pydantic field errors, backend
      // detail strings, network failures) so the user understands the blocker.
      if (error?.response?.status === 401) {
        return; // handled globally with redirect to /login
      }
      const errorDetail = error.response?.data?.detail;
      let errorMessage = t('error');
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Pydantic v2 validation → list of { loc, msg, type }. Include the field
        // name so the user knows WHERE the blocker is.
        const parts = errorDetail.slice(0, 3).map((e) => {
          const field = Array.isArray(e?.loc) ? e.loc.filter((x) => x !== 'body').join('.') : '';
          return field ? `${field}: ${e?.msg || ''}` : (e?.msg || '');
        });
        errorMessage = parts.join(' · ');
      } else if (!error?.response && error?.message) {
        // Network / CORS / timeout
        errorMessage = (language === 'fr'
          ? 'Erreur réseau : '
          : 'Network error: ') + error.message;
      }
      toast.error(errorMessage, { duration: 7000 });
    } finally {
      setLoading(false);
    }
  };

  const progress = (safeCurrentStep / activeSteps.length) * 100;

  const switchFormat = (v) => {
    setFormData((prev) => {
      const next = { ...prev, general_info: { ...prev.general_info, format: v } };
      // retour en Corner après une vitrine : on remet les montants CAPEX par défaut
      if (v === 'corner' && prev.general_info.format === 'vitrine_deportee' && !prev.capex_investment.capex_corner) {
        const d = defaultFormData.capex_investment;
        next.capex_investment = { ...prev.capex_investment, capex_corner: d.capex_corner, capex_vitrine: d.capex_vitrine,
          capex_inox: d.capex_inox, capex_computer_cash: d.capex_computer_cash,
          corner_investor: d.corner_investor, vitrine_investor: d.vitrine_investor, inox_investor: d.inox_investor };
      }
      return next;
    });
  };

  // Surface magasin pré-remplie depuis le fichier GSA (enseigne + ville / CP), uniquement si vide
  const [gsaInfo, setGsaInfo] = useState(null);
  const gi0 = formData.general_info;
  useEffect(() => {
    if (!gi0.retailer_name || !(gi0.city || gi0.postal_code)) return undefined;
    const t = setTimeout(() => {
      api().get('/gsa/surface', { params: { enseigne: gi0.retailer_name, city: gi0.city || '', postal_code: gi0.postal_code || '' } })
        .then(({ data }) => {
          setGsaInfo(data);
          if (data?.surface) setFormData(prev => (prev.general_info.store_surface_m2 ? prev : { ...prev, general_info: { ...prev.general_info, store_surface_m2: data.surface } }));
        }).catch(() => setGsaInfo(null));
    }, 600);
    return () => clearTimeout(t);
  }, [gi0.retailer_name, gi0.city, gi0.postal_code]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderVitrineStep = () => {
    const inv = formData.capex_investment.vitrine_investor;
    return (
      <div className="space-y-6 animate-fade-in" data-testid="vitrine-deportee-step">
        <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 text-xs text-orange-800" data-testid="vitrine-deportee-info">
          <strong>Vitrine déportée</strong> : CA prévisionnel = 50 % d'un corner · frais de personnel = 10 % d'un corner · mêmes taux (coût matière, commission, frais) que pour un corner.
        </div>
        <ParentCornerPicker value={formData.general_info.parent_simulation_id || null} excludeId={id}
          onChange={(v) => updateFormData('general_info', 'parent_simulation_id', v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect label={t('vitrineInvestor')} id="vitrine_investor" value={inv}
            onChange={(v) => updateFormData('capex_investment', 'vitrine_investor', v)}
            options={[{ value: 'CLIENT', label: t('client') }, { value: 'CCD', label: t('ccd') }]} />
          <div className="space-y-2">
            <Label>CAPEX Vitrine (€)</Label>
            <div className="h-10 px-3 flex items-center justify-between rounded-md border bg-slate-50 font-semibold" data-testid="vitrine-deportee-capex">
              <span>{inv === 'CCD' ? '8 000' : '0'}</span><span className="text-slate-400">€</span>
            </div>
            <p className="text-[11px] text-slate-500">{inv === 'CCD' ? 'Pris en charge par CCD' : 'Pris en charge par le client : 0 € pour CCD'}</p>
          </div>
        </div>
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />Durée de l'engagement
          </h4>
          <FormSelect label={t('contractDuration')} id="contract_duration_months"
            value={formData.contract_info.contract_duration_months?.toString() || '36'}
            onChange={(v) => updateFormData('contract_info', 'contract_duration_months', parseInt(v))}
            options={['12', '24', '36', '48', '60'].map((m) => ({ value: m, label: `${m} mois` }))} />
        </div>
      </div>
    );
  };

  const renderStep = () => {
    switch (safeCurrentStep) {
      case 1:
        return (
          <div className="space-y-6 animate-fade-in">
            {/* Recherche magasin/adresse en premier */}
            <AddressAutocomplete
              language={language}
              onAddressSelect={(addressData) => {
                if (addressData.address) {
                  updateFormData('general_info', 'address', addressData.address);
                }
                if (addressData.postal_code) {
                  updateFormData('general_info', 'postal_code', addressData.postal_code);
                }
                if (addressData.city) {
                  updateFormData('general_info', 'city', addressData.city);
                }
                if (addressData.department) {
                  updateFormData('general_info', 'department', addressData.department);
                }
                toast.success(language === 'fr' ? 'Adresse remplie automatiquement' : 'Address auto-filled');
              }}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormSelect
                label={t('retailerName')}
                id="retailer_name"
                value={formData.general_info.retailer_name}
                onChange={(v) => updateFormData('general_info', 'retailer_name', v)}
                options={enseigneOptions.length ? enseigneOptions : [
                  { value: 'Leclerc', label: 'Leclerc' },
                  { value: 'Intermarché', label: 'Intermarché' },
                  { value: 'Système U', label: 'Système U' },
                  { value: 'Carrefour', label: 'Carrefour' },
                  { value: 'Auchan', label: 'Auchan' },
                  { value: 'Cora', label: 'Cora' },
                ]}
                required
                highlightError={highlightedFields.has('retailer_name')}
              />
              <FormSelect
                label={t('projectType')}
                id="project_type"
                value={formData.general_info.project_type}
                onChange={updateProjectType}
                options={[
                  { value: 'Nouvelle ouverture', label: 'Nouvelle ouverture (New opening)' },
                  { value: 'Renouvellement contrat', label: 'Renouvellement contrat (Contract renewal)' },
                ]}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label={t('projectName')}
                id="project_name"
                value={formData.general_info.project_name}
                onChange={(e) => { projectNameManualRef.current = true; updateFormData('general_info', 'project_name', e.target.value); }}
                placeholder="Ex : Intermarché Bourges"
                required
              />
              <div>
                <FormInput
                  label={t('openingDate')}
                  id="opening_date"
                  type="date"
                  value={formData.general_info.opening_date}
                  onChange={(e) => updateFormData('general_info', 'opening_date', e.target.value)}
                />
                {!isThursday(formData.general_info.opening_date) && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-amber-600" data-testid="opening-date-not-thursday">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    {language === 'fr' ? "Attention : cette date n'est pas un jeudi (jour d'ouverture habituel)." : 'Warning: this date is not a Thursday (usual opening day).'}
                  </p>
                )}
              {!isVitrine && (
                <div>
                  <FormInput
                    label={language === 'fr' ? 'Date signature estimée' : 'Estimated signing date'}
                    id="estimated_signature_date"
                    type="date"
                    value={formData.general_info.estimated_signature_date || ''}
                    onChange={(e) => updateFormData('general_info', 'estimated_signature_date', e.target.value)}
                  />
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <p className="text-xs text-slate-500" data-testid="estimated-signature-hint">
                      {language === 'fr' ? 'Par défaut : création + 45 jours. Sert à la prévision du Business Commercial.' : 'Default: creation + 45 days. Used for the Business Commercial forecast.'}
                    </p>
                    {formData.general_info.estimated_signature_date && (
                      <button type="button" className="text-xs font-semibold text-slate-500 underline hover:text-red-600 shrink-0" onClick={() => updateFormData('general_info', 'estimated_signature_date', '')} data-testid="estimated-signature-clear">
                        {language === 'fr' ? 'Effacer la date' : 'Clear date'}
                      </button>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
            
            <FormInput
              label={t('address')}
              id="address"
              value={formData.general_info.address}
              onChange={(e) => updateFormData('general_info', 'address', e.target.value)}
              placeholder="123 Rue du Commerce"
            />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <FormInput
                label={t('postalCode')}
                id="postal_code"
                value={formData.general_info.postal_code}
                onChange={(e) => updateFormData('general_info', 'postal_code', e.target.value)}
                placeholder="69000"
              />
              <FormInput
                label={t('city')}
                id="city"
                value={formData.general_info.city}
                onChange={(e) => updateFormData('general_info', 'city', e.target.value)}
                placeholder="Lyon"
                className="col-span-1 md:col-span-2"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 rounded-lg bg-graphite-900 text-white" data-testid="smappen-band">
              <p className="text-xs font-bold uppercase tracking-[0.12em]">
                {language === 'fr' ? 'Données issues étude SMAPPEN' : 'Data from SMAPPEN study'}
                {smappenMissingCount > 0 && (
                  <span className="ml-3 normal-case tracking-normal font-semibold text-orange-300" data-testid="smappen-missing-count">
                    {smappenMissingCount} champ(s) à compléter (conseillé)
                  </span>
                )}
              </p>
              <a href="https://www.smappen.fr/app/" target="_blank" rel="noopener noreferrer" data-testid="smappen-link"
                className="inline-flex items-center gap-2 h-8 pl-1.5 pr-3 rounded-md bg-white text-graphite-900 text-xs font-semibold hover:bg-orange-50 transition-colors">
                <img src="/images/smappen-logo.png" alt="SMAPPEN" className="h-6 w-6 object-contain" />
                {language === 'fr' ? 'Accéder au site SMAPPEN' : 'Open SMAPPEN website'}
                <ExternalLink className="w-3.5 h-3.5 text-orange-500" />
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={smappenMiss('store_surface_m2')} data-testid="smappen-field-store_surface_m2">
              <FormInput
                label={t('storeSurface')}
                id="store_surface_m2"
                type="number"
                inputMode="numeric"
                value={formData.general_info.store_surface_m2}
                onChange={(e) => updateFormData('general_info', 'store_surface_m2', parseFloat(e.target.value) || null)}
                placeholder="5000"
              />
              {gsaInfo && (
                <p className={`text-[11px] mt-1 ${gsaInfo.surface ? 'text-emerald-700' : 'text-slate-400'}`} data-testid="gsa-surface-hint">
                  {gsaInfo.surface ? `Fichier GSA : ${gsaInfo.matches[0].nom.replace(/\s\d+$/, '')} · ${gsaInfo.surface.toLocaleString('fr-FR')} m²` : 'Aucun magasin trouvé dans le fichier GSA'}
                </p>
              )}
              </div>
              <div className={smappenMiss('zone_avg_age')} data-testid="smappen-field-zone_avg_age">
              <FormInput
                label={t('zoneAvgAge')}
                id="zone_avg_age"
                type="number"
                inputMode="numeric"
                value={formData.general_info.zone_avg_age}
                onChange={(e) => updateFormData('general_info', 'zone_avg_age', parseFloat(e.target.value) || null)}
                placeholder="42"
              />
              </div>
              <div className={smappenMiss('zone_csp')} data-testid="smappen-field-zone_csp">
              <FormSelect
                label={t('zoneCSP')}
                id="zone_csp"
                value={formData.general_info.zone_csp}
                onChange={(v) => updateFormData('general_info', 'zone_csp', v)}
                options={[
                  { value: 'Agriculteurs exploitants', label: 'Agriculteurs exploitants' },
                  { value: 'Artisans, Commerçants, Chefs entreprise', label: 'Artisans, Commerçants, Chefs entreprise' },
                  { value: 'Cadres & professions intellectuelles supérieures', label: 'Cadres & professions intellectuelles supérieures' },
                  { value: 'Professions intermédiaires', label: 'Professions intermédiaires' },
                  { value: 'Employés', label: 'Employés' },
                  { value: 'Ouvriers', label: 'Ouvriers' },
                ]}
              />
              </div>
              <div className={smappenMiss('revenu_median')} data-testid="smappen-field-revenu_median">
              <FormMoneyInput
                label={language === 'fr' ? 'Revenu Médian (€)' : 'Median Income (€)'}
                id="revenu_median"
                value={formData.general_info.revenu_median}
                onChange={(val) => updateFormData('general_info', 'revenu_median', val)}
                placeholder="25 000"
              />
              </div>
            </div>
            
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fade-in">
            <FormSwitch
              label={t('hasCompetitor')}
              id="has_competitor"
              checked={formData.competition_info.has_competitor}
              onChange={(v) => updateFormData('competition_info', 'has_competitor', v)}
            />
            {formData.competition_info.has_competitor && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormSelect
                    label={t('competitorName')}
                    id="competitor_name"
                    value={formData.competition_info.competitor_name}
                    onChange={(v) => updateFormData('competition_info', 'competitor_name', v)}
                    options={[
                      { value: 'Hanagroup', label: 'Hanagroup' },
                      { value: 'Kellydeli', label: 'Kellydeli' },
                      { value: 'Sushiman', label: 'Sushiman' },
                      { value: 'Sushi Shop', label: 'Sushi Shop' },
                      { value: 'Papylee', label: 'Papylee' },
                      { value: 'Makichef', label: 'Makichef' },
                      { value: 'Katana', label: 'Katana' },
                      { value: 'Esprit Sushi', label: 'Esprit Sushi' },
                      { value: 'Bento Sushi', label: 'Bento Sushi' },
                      { value: 'Côté Sushi', label: 'Côté Sushi' },
                      { value: 'Club Sushi', label: 'Club Sushi' },
                      { value: 'Asian Corner', label: 'Asian Corner' },
                      { value: 'Skykitchen', label: 'Skykitchen' },
                      { value: 'Fait maison par mag', label: 'Fait maison par mag' },
                      { value: 'Autre', label: 'Autre' },
                    ]}
                  />
                  {formData.competition_info.competitor_name === 'Autre' && (
                    <FormInput
                      label={language === 'fr' ? 'Nom du concurrent (facultatif)' : 'Competitor name (optional)'}
                      id="competitor_other_name"
                      value={formData.competition_info.competitor_other_name || ''}
                      onChange={(e) => updateFormData('competition_info', 'competitor_other_name', e.target.value)}
                      placeholder={language === 'fr' ? 'Ex : Sushi Daily' : 'e.g. Sushi Daily'}
                    />
                  )}
                  <FormSelect
                    label={t('engagementDuration')}
                    id="engagement_duration"
                    value={formData.competition_info.engagement_duration?.toString() || ''}
                    onChange={(v) => updateFormData('competition_info', 'engagement_duration', parseInt(v))}
                    options={[
                      { value: '12', label: '12 mois' },
                      { value: '24', label: '24 mois' },
                      { value: '36', label: '36 mois' },
                      { value: '48', label: '48 mois' },
                      { value: '60', label: '60 mois' },
                    ]}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5 block">
                    {language === 'fr' ? 'Date de fin de contrat' : 'Contract End Date'}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      id="engagement_end_date"
                      value={formData.competition_info.engagement_end_date === 'unknown' ? '' : (formData.competition_info.engagement_end_date || '')}
                      onChange={(e) => updateFormData('competition_info', 'engagement_end_date', e.target.value)}
                      className="flex-1 h-12"
                      disabled={formData.competition_info.engagement_end_date === 'unknown'}
                      data-testid="input-engagement_end_date"
                    />
                    <Button
                      type="button"
                      variant={formData.competition_info.engagement_end_date === 'unknown' ? 'default' : 'outline'}
                      onClick={() => {
                        if (formData.competition_info.engagement_end_date === 'unknown') {
                          updateFormData('competition_info', 'engagement_end_date', '');
                        } else {
                          updateFormData('competition_info', 'engagement_end_date', 'unknown');
                        }
                      }}
                      className="whitespace-nowrap h-12"
                      data-testid="btn-unknown-date"
                    >
                      {formData.competition_info.engagement_end_date === 'unknown' 
                        ? (language === 'fr' ? '✓ Date inconnue' : '✓ Unknown date')
                        : (language === 'fr' ? 'Pas de date connue' : 'No known date')}
                    </Button>
                  </div>
                  {formData.competition_info.engagement_end_date === 'unknown' && (
                    <p className="text-xs text-amber-600">
                      {language === 'fr' ? 'La date de fin de contrat est inconnue' : 'Contract end date is unknown'}
                    </p>
                  )}
                </div>
                <FormCheckboxGroup
                  label={t('currentConcept')}
                  id="current_concept"
                  value={formData.competition_info.current_concept}
                  onChange={(v) => updateFormData('competition_info', 'current_concept', v)}
                  options={[
                    { value: 'Sushi', label: 'Sushi' },
                    { value: 'Wok', label: 'Wok' },
                    { value: 'Poké', label: 'Poké' },
                  ]}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="current_discount" className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5 block">
                      {t('currentDiscount')}
                    </Label>
                    <select
                      id="current_discount"
                      value={formData.competition_info.current_discount ? Math.round(formData.competition_info.current_discount * 100).toString() : ''}
                      onChange={(e) => updateFormData('competition_info', 'current_discount', parseFloat(e.target.value) / 100)}
                      className="w-full h-12 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="select-current_discount"
                    >
                      <option value="">--</option>
                      <option value="12">12%</option>
                      <option value="13">13%</option>
                      <option value="14">14%</option>
                      <option value="15">15%</option>
                      <option value="16">16%</option>
                      <option value="17">17%</option>
                      <option value="18">18%</option>
                      <option value="19">19%</option>
                      <option value="20">20%</option>
                      <option value="21">21%</option>
                      <option value="22">22%</option>
                      <option value="23">23%</option>
                      <option value="24">24%</option>
                      <option value="25">25%</option>
                    </select>
                  </div>
                  <FormMoneyInput
                    label={t('competitorCA')}
                    id="competitor_annual_ca"
                    value={formData.competition_info.competitor_annual_ca}
                    onChange={(val) => updateFormData('competition_info', 'competitor_annual_ca', val)}
                    placeholder="500 000"
                  />
                </div>
              </>
            )}
            <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <FormSwitch
                label={t('hasOtherRestaurant')}
                id="has_other_restaurant"
                checked={formData.competition_info.has_other_restaurant}
                onChange={(v) => updateFormData('competition_info', 'has_other_restaurant', v)}
              />
              <FormSwitch
                label={t('hasPanAsian')}
                id="has_pan_asian"
                checked={formData.competition_info.has_pan_asian}
                onChange={(v) => updateFormData('competition_info', 'has_pan_asian', v)}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-fade-in">
            <FormMoneyInput
              label={t('annualCAHorsCarburant')}
              id="annual_ca_hors_carburant"
              value={formData.financial_data.annual_ca_hors_carburant}
              onChange={(val) => updateFormData('financial_data', 'annual_ca_hors_carburant', val)}
              placeholder="80 000 000"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormMoneyInput
                label={t('annualCAAlimentaire')}
                id="annual_ca_alimentaire"
                value={formData.financial_data.annual_ca_alimentaire}
                onChange={(val) => updateFormData('financial_data', 'annual_ca_alimentaire', val)}
                placeholder="50 000 000"
                required
                highlightError={highlightedFields.has('annual_ca_alimentaire')}
              />
              <FormMoneyInput
                label={t('annualCAPoissonnerie')}
                id="annual_ca_poissonnerie"
                value={formData.financial_data.annual_ca_poissonnerie}
                onChange={(val) => updateFormData('financial_data', 'annual_ca_poissonnerie', val)}
                placeholder="2 000 000"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormMoneyInput
                label={t('annualFrequentation')}
                id="annual_frequentation"
                value={formData.financial_data.annual_frequentation}
                onChange={(val) => updateFormData('financial_data', 'annual_frequentation', val)}
                placeholder="1 500 000"
                suffix=""
                required
                highlightError={highlightedFields.has('annual_frequentation')}
              />
              <FormMoneyInput
                label={t('globalTicketMoyen')}
                id="global_ticket_moyen"
                value={formData.financial_data.global_ticket_moyen}
                onChange={(val) => updateFormData('financial_data', 'global_ticket_moyen', val)}
                placeholder="35"
                suffix="€"
                required
                highlightError={highlightedFields.has('global_ticket_moyen')}
              />
            </div>
            
            {/* Cluster Preview - Real-time estimation */}
            {(formData.financial_data.annual_frequentation || formData.financial_data.annual_ca_alimentaire) && (() => {
              const clusterInfo = getEstimatedCluster();
              const hasEnoughData = clusterInfo.avgCaHt > 0;
              
              if (!hasEnoughData) return null;
              
              return (
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800" data-testid="cluster-preview">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <Calculator className="w-4 h-4 text-white" />
                    </div>
                    <h4 className="font-semibold text-blue-900 dark:text-blue-100">
                      {language === 'fr' ? 'Prévisualisation Cluster' : 'Cluster Preview'}
                    </h4>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {/* Cluster Name */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                        {language === 'fr' ? 'Cluster estimé' : 'Estimated Cluster'}
                      </p>
                      <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {clusterInfo.name}
                      </p>
                    </div>
                    
                    {/* Annual CA */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                        {language === 'fr' ? 'CA Annuel estimé' : 'Est. Annual Rev.'}
                      </p>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {Math.round(clusterInfo.avgCaHt).toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    
                    {/* Monthly CA */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                        {language === 'fr' ? 'CA Mensuel estimé' : 'Est. Monthly Rev.'}
                      </p>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">
                        {Math.round(clusterInfo.monthlyCa).toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    
                    {/* Food Cost */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Food Cost</p>
                      <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                        {(clusterInfo.food_cost_rate * 100).toFixed(2)}%
                      </p>
                    </div>
                    
                    {/* Other Cost */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Other Cost</p>
                      <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {(clusterInfo.other_cost_rate * 100).toFixed(2)}%
                      </p>
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 italic">
                    {language === 'fr' 
                      ? '* Estimation basée sur la moyenne des 3 hypothèses. Les valeurs finales peuvent varier.'
                      : '* Estimation based on average of 3 hypotheses. Final values may vary.'}
                  </p>
                </div>
              );
            })()}
          </div>
        );

      case 4:
        if (isVitrine) return renderVitrineStep();
        return (
          <div className="space-y-6 animate-fade-in">
            {/* Renewal savings banner */}
            {formData.general_info.project_type === 'Renouvellement contrat' && (() => {
              const items = [];
              if (formData.general_info.renouvellement_change_inox === 'Non') items.push({ label: 'Inox', amount: 7000 });
              if (formData.general_info.renouvellement_change_vitrines === 'Non') items.push({ label: 'Vitrine(s)', amount: 8000 });
              if (formData.general_info.renouvellement_change_plv === 'Non') items.push({ label: 'PLV du corner', amount: 12000 });
              if (items.length === 0) return null;
              const total = items.reduce((s, i) => s + i.amount, 0);
              const fmt = (n) => n.toLocaleString('fr-FR') + ' €';
              return (
                <div className="p-4 rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-amber-50/40" data-testid="renewal-savings-banner">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-emerald-700 text-lg font-bold">−</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-emerald-800">
                        Renouvellement allégé : <span className="text-base">{fmt(total)}</span> exclus du CAPEX
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        Éléments non remplacés : {items.map((i, k) => (
                          <span key={i.label} className="inline-block ml-1">
                            <span className="font-medium text-slate-700">{i.label}</span>
                            <span className="text-slate-500"> ({fmt(i.amount)})</span>
                            {k < items.length - 1 ? ',' : ''}
                          </span>
                        ))}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
              <FormSelect
                label={t('cornerInvestor')}
                id="corner_investor"
                value={formData.capex_investment.corner_investor}
                onChange={(v) => updateFormData('capex_investment', 'corner_investor', v)}
                options={[
                  { value: 'CLIENT', label: t('client') },
                  { value: 'CCD', label: t('ccd') },
                ]}
                />
                {renewalToggle('corner')}
              </div>
              <div className="space-y-2">
              <FormSelect
                label={t('vitrineInvestor')}
                id="vitrine_investor"
                value={formData.capex_investment.vitrine_investor}
                onChange={(v) => updateFormData('capex_investment', 'vitrine_investor', v)}
                options={[
                  { value: 'CLIENT', label: t('client') },
                  { value: 'CCD', label: t('ccd') },
                ]}
                />
                {renewalToggle('vitrine')}
              </div>
              <div className="space-y-2">
              <FormSelect
                label={t('inoxInvestor')}
                id="inox_investor"
                value={formData.capex_investment.inox_investor}
                onChange={(v) => updateFormData('capex_investment', 'inox_investor', v)}
                options={[
                  { value: 'CLIENT', label: t('client') },
                  { value: 'CCD', label: t('ccd') },
                ]}
                />
                {renewalToggle('inox')}
              </div>
            </div>
            <FormCheckboxGroup
              label={t('concept')}
              id="concept"
              value={formData.capex_investment.concept}
              onChange={(v) => updateFormData('capex_investment', 'concept', v)}
              options={[
                { value: 'WASABI', label: 'WASABI' },
                { value: 'MME WANG', label: 'MME WANG' },
                { value: 'SORAE', label: 'SORAE' },
                { value: 'POKAWA', label: 'POKAWA' },
              ]}
            />
            
            {/* Corner Details - New dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <FormSelect
                label={language === 'fr' ? 'Emplacement du corner' : 'Corner Location'}
                id="corner_location"
                value={formData.capex_investment.corner_location}
                onChange={(val) => updateFormData('capex_investment', 'corner_location', val)}
                options={[
                  { value: '', label: language === 'fr' ? '-- Sélectionner --' : '-- Select --' },
                  { value: 'Zone Entrée', label: 'Zone Entrée' },
                  { value: 'Zone Fleg', label: 'Zone Fleg' },
                  { value: 'Zone Allée Promo', label: 'Zone Allée Promo' },
                  { value: 'Zone poissonnerie', label: 'Zone poissonnerie' },
                  { value: 'Entre R Trad.', label: 'Entre R Trad.' },
                  { value: 'Zone du fond', label: 'Zone du fond' },
                ]}
              />
              <FormSelect
                label={language === 'fr' ? 'Note de l\'emplacement' : 'Location Rating'}
                id="location_rating"
                value={formData.capex_investment.location_rating}
                onChange={(val) => updateFormData('capex_investment', 'location_rating', val)}
                options={[
                  { value: '', label: language === 'fr' ? '-- Sélectionner --' : '-- Select --' },
                  { value: 'Incroyable', label: 'Incroyable' },
                  { value: 'Très bon', label: 'Très bon' },
                  { value: 'Bon', label: 'Bon' },
                  { value: 'Moyen', label: 'Moyen' },
                  { value: 'Très moyen', label: 'Très moyen' },
                  { value: 'C\'est pas gagné', label: 'C\'est pas gagné' },
                ]}
              />
              <FormSelect
                label={language === 'fr' ? 'Projet du corner' : 'Corner Project'}
                id="corner_project"
                value={formData.capex_investment.corner_project}
                onChange={(val) => updateFormData('capex_investment', 'corner_project', val)}
                options={[
                  { value: '', label: language === 'fr' ? '-- Sélectionner --' : '-- Select --' },
                  { value: '4 faces on tourne', label: '4 faces on tourne' },
                  { value: '3 faces contre un mur', label: '3 faces contre un mur' },
                  { value: 'Atypique', label: 'Atypique' },
                  { value: 'A définir', label: 'A définir' },
                ]}
              />
            </div>

            {/* Prévoir démontage du corner */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700" data-testid="dismantling-section">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h4 className="text-base font-semibold text-slate-900 dark:text-white">
                    {language === 'fr' ? 'Prévoir démontage du corner ?' : 'Plan corner dismantling?'}
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {language === 'fr'
                      ? 'Indiquez si un démontage de l\'ancien corner est à prévoir.'
                      : 'Indicate whether dismantling of the former corner is required.'}
                  </p>
                </div>
                <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden self-start">
                  {[{ v: true, l: language === 'fr' ? 'Oui' : 'Yes' }, { v: false, l: language === 'fr' ? 'Non' : 'No' }].map((opt) => (
                    <button
                      key={String(opt.v)}
                      type="button"
                      onClick={() => {
                        updateFormData('capex_investment', 'corner_dismantling', opt.v);
                        if (!opt.v) updateFormData('capex_investment', 'dismantling_by', '');
                      }}
                      className={`px-5 py-2 text-sm font-semibold transition-colors ${formData.capex_investment.corner_dismantling === opt.v ? 'bg-orange-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                      data-testid={`dismantling-${opt.v ? 'oui' : 'non'}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              {formData.capex_investment.corner_dismantling && (
                <div className="mt-4 md:max-w-sm" data-testid="dismantling-by-wrapper">
                  <FormSelect
                    label={language === 'fr' ? 'Démontage pris en charge par' : 'Dismantling handled by'}
                    id="dismantling_by"
                    value={formData.capex_investment.dismantling_by}
                    onChange={(val) => updateFormData('capex_investment', 'dismantling_by', val)}
                    options={[
                      { value: '', label: language === 'fr' ? '-- Sélectionner --' : '-- Select --' },
                      { value: 'DSAO', label: 'DSAO' },
                      { value: 'CLIENT', label: 'Client' },
                      { value: 'PRESTATAIRE_SORTANT', label: language === 'fr' ? 'Prestataire sortant' : 'Outgoing provider' },
                    ]}
                  />
                </div>
              )}
            </div>

            {applyNewCapexRules && (
              <div className="pt-4 border-t-2 border-amber-400" data-testid="labo-section">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h4 className="text-base font-semibold text-slate-900 dark:text-white">
                      {language === 'fr' ? 'Labo à équiper ?' : 'Lab to equip?'}
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {language === 'fr'
                        ? 'Si oui, un inox labo est ajouté à l\'investissement CAPEX.'
                        : 'If yes, a lab inox is added to the CAPEX investment.'}
                    </p>
                  </div>
                  <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden self-start">
                    {[{ v: true, l: language === 'fr' ? 'Oui' : 'Yes' }, { v: false, l: language === 'fr' ? 'Non' : 'No' }].map((opt) => (
                      <button
                        key={String(opt.v)}
                        type="button"
                        onClick={() => updateLaboToEquip(opt.v)}
                        className={`px-5 py-2 text-sm font-semibold transition-colors ${formData.capex_investment.labo_to_equip === opt.v ? 'bg-amber-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                        data-testid={`labo-toequip-${opt.v ? 'oui' : 'non'}`}
                      >
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>

                {formData.capex_investment.labo_to_equip && (
                  <div className="mt-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 space-y-4" data-testid="labo-details">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormMoneyInput
                        label={language === 'fr' ? 'Investissement CAPEX — Inox Labo' : 'CAPEX Investment — Lab Inox'}
                        id="capex_inox_labo"
                        value={formData.capex_investment.capex_inox_labo}
                        onChange={(val) => updateFormData('capex_investment', 'capex_inox_labo', val ?? 0)}
                        placeholder="4 000"
                        disabled={!isAdmin}
                        hint={language === 'fr' ? '4 000 € par défaut si labo à équiper' : 'Default 4 000 € if lab to equip'}
                      />
                      <FormSelect
                        label={language === 'fr' ? 'Inox Labo — Investisseur' : 'Lab Inox — Investor'}
                        id="labo_inox_investor"
                        value={formData.capex_investment.labo_inox_investor}
                        onChange={(v) => updateFormData('capex_investment', 'labo_inox_investor', v)}
                        options={[
                          { value: 'CCD', label: t('ccd') },
                          { value: 'CLIENT', label: t('client') },
                        ]}
                      />
                    </div>
                    <div>
                      <Label htmlFor="labo_comment" className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1.5 block">
                        {language === 'fr' ? 'Commentaire' : 'Comment'}
                      </Label>
                      <textarea
                        id="labo_comment"
                        value={formData.capex_investment.labo_comment || ''}
                        onChange={(e) => updateFormData('capex_investment', 'labo_comment', e.target.value)}
                        rows={2}
                        placeholder={language === 'fr' ? 'Précisions sur le labo à équiper…' : 'Notes about the lab…'}
                        className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-300"
                        data-testid="labo-comment"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {applyNewCapexRules && (
              <div className="relative" data-testid="vitrine-composition-wrap">
                {kept('vitrine') && (
                  <div className="mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 border border-slate-200 text-xs font-semibold text-slate-600" data-testid="vitrine-kept-notice">
                    <Info className="w-4 h-4" /> Matériel existant conservé — vitrines non renouvelées, CAPEX Vitrine = 0 €
                  </div>
                )}
                <div className={kept('vitrine') ? 'opacity-40 pointer-events-none select-none' : ''} aria-disabled={kept('vitrine')}>
                  <VitrineCompositionSection
                    composition={formData.capex_investment.vitrine_composition}
                    onChange={updateVitrineComposition}
                    language={language}
                  />
                </div>
              </div>
            )}

            {applyNewCapexRules && getEstimatedCluster().avgCaHt > 500000 && (
              <div className="pt-4 border-t-2 border-amber-400" data-testid="rice-machine-section">
                <div className={`flex items-center justify-between gap-3 p-4 rounded-lg border transition-colors ${formData.capex_investment.rice_machine ? 'border-indigo-300 bg-indigo-50/60 dark:bg-indigo-900/20' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="rice_machine"
                      checked={!!formData.capex_investment.rice_machine}
                      onCheckedChange={updateRiceMachine}
                      data-testid="rice-machine-check"
                    />
                    <Label htmlFor="rice_machine" className="cursor-pointer">
                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                        {language === 'fr' ? 'Machine à tapisser le riz' : 'Rice sheeting machine'}
                      </span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        {language === 'fr'
                          ? 'Proposée car prévisionnel de CA > 500 000 €'
                          : 'Suggested because forecast turnover > €500,000'}
                      </span>
                    </Label>
                  </div>
                  <span className={`text-lg font-bold tabular-nums ${formData.capex_investment.rice_machine ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-300'}`}>
                    9 000 €
                  </span>
                </div>
              </div>
            )}

            {applyNewCapexRules && !isAdmin && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="capex-readonly-notice">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{language === 'fr'
                  ? 'Les montants CAPEX sont gérés automatiquement et ne peuvent être modifiés que par un Admin. Le CAPEX Vitrine se calcule via la composition ci-dessus.'
                  : 'CAPEX amounts are managed automatically and can only be edited by an Admin. CAPEX Vitrine is computed from the composition above.'}</span>
              </div>
            )}

            <div className="pt-4 border-t border-slate-200 dark:border-slate-700" data-testid="capex-amounts-section">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                {language === 'fr' ? 'Montants CAPEX (€)' : 'CAPEX amounts (€)'}
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <FormMoneyInput
                  compact
                  label={t('capexCorner')}
                  id="capex_corner"
                  value={formData.capex_investment.capex_corner}
                  onChange={(val) => updateFormData('capex_investment', 'capex_corner', val ?? 0)}
                  placeholder="12 000"
                  disabled={(applyNewCapexRules && !isAdmin) || kept('corner')}
                  hint={kept('corner') ? 'Matériel existant conservé' : undefined}
                />
                <FormMoneyInput
                  compact
                  label={t('capexVitrine')}
                  id="capex_vitrine"
                  value={formData.capex_investment.capex_vitrine}
                  onChange={(val) => updateFormData('capex_investment', 'capex_vitrine', val ?? 0)}
                  placeholder="8 000"
                  disabled={(applyNewCapexRules && !isAdmin) || kept('vitrine')}
                  hint={kept('vitrine') ? 'Matériel existant conservé' : applyNewCapexRules ? (language === 'fr' ? 'Calculé automatiquement (composition vitrines)' : 'Auto-computed (vitrine composition)') : undefined}
                />
                <FormMoneyInput
                  compact
                  label={t('capexInox')}
                  id="capex_inox"
                  value={formData.capex_investment.capex_inox}
                  onChange={(val) => updateFormData('capex_investment', 'capex_inox', val ?? 0)}
                  placeholder="7 000"
                  disabled={(applyNewCapexRules && !isAdmin) || kept('inox')}
                  hint={kept('inox') ? 'Matériel existant conservé' : undefined}
                />
                <FormMoneyInput
                  compact
                  label={t('capexComputer')}
                  id="capex_computer_cash"
                  value={formData.capex_investment.capex_computer_cash}
                  onChange={(val) => updateFormData('capex_investment', 'capex_computer_cash', val ?? 0)}
                  placeholder="1 000"
                  disabled={applyNewCapexRules && !isAdmin}
                />
                {applyNewCapexRules && formData.capex_investment.labo_to_equip && (
                  <FormMoneyInput
                    compact
                    label={language === 'fr' ? 'CAPEX Inox Labo' : 'CAPEX Lab Inox'}
                    id="capex_inox_labo_recap"
                    value={formData.capex_investment.capex_inox_labo}
                    onChange={() => {}}
                    placeholder="0"
                    disabled={true}
                    hint={language === 'fr' ? 'Voir bloc Labo' : 'See Lab block'}
                  />
                )}
                {applyNewCapexRules && formData.capex_investment.rice_machine && (
                  <FormMoneyInput
                    compact
                    label={language === 'fr' ? 'CAPEX Machine riz' : 'CAPEX Rice machine'}
                    id="capex_rice_machine_recap"
                    value={formData.capex_investment.capex_rice_machine}
                    onChange={() => {}}
                    placeholder="0"
                    disabled={true}
                    hint={language === 'fr' ? 'Portée par CCD' : 'Carried by CCD'}
                  />
                )}
                <FormMoneyInput
                  compact
                  label={t('capexLocalLegal')}
                  id="capex_local_legal"
                  value={formData.capex_investment.capex_local_legal}
                  onChange={(val) => updateFormData('capex_investment', 'capex_local_legal', val ?? 0)}
                  placeholder="0"
                  disabled={applyNewCapexRules && !isAdmin}
                />
              </div>
            </div>

            {(() => {
              const cx = formData.capex_investment;
              const inoxLabo = cx.labo_to_equip ? (Number(cx.capex_inox_labo) || 0) : 0;
              const rice = cx.rice_machine ? (Number(cx.capex_rice_machine) || 0) : 0;
              let ccdMain = 0, clientMain = 0;
              [[cx.capex_corner, cx.corner_investor], [cx.capex_vitrine, cx.vitrine_investor], [cx.capex_inox, cx.inox_investor], [inoxLabo, cx.labo_inox_investor]].forEach(([amt, inv]) => {
                if ((inv || 'CCD') === 'CCD') ccdMain += Number(amt) || 0; else clientMain += Number(amt) || 0;
              });
              const computer = Number(cx.capex_computer_cash) || 0;
              const clientIsMajority = clientMain >= ccdMain;
              const followMajority = (Number(cx.capex_local_legal) || 0) + (Number(cx.capex_robots) || 0);
              const ccd = ccdMain + computer + rice + (clientIsMajority ? 0 : followMajority);
              const client = clientMain + (clientIsMajority ? followMajority : 0);
              const total = ccd + client;
              const fmt = (n) => `${Math.round(n).toLocaleString('fr-FR')} €`;
              const pct = (n) => total > 0 ? Math.round((n / total) * 100) : 0;
              return (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/10 p-4" data-testid="capex-consolidated-total">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {language === 'fr' ? 'CAPEX total consolidé' : 'Consolidated total CAPEX'}
                    </span>
                    <span className="text-xl font-bold text-emerald-700 dark:text-emerald-300 tabular-nums" data-testid="capex-total-value">{fmt(total)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-[11px] uppercase tracking-wide text-indigo-500">CCD</p>
                      <p className="text-lg font-bold text-indigo-700 dark:text-indigo-300 tabular-nums" data-testid="capex-ccd-value">{fmt(ccd)} <span className="text-xs font-medium text-slate-400">· {pct(ccd)}%</span></p>
                    </div>
                    <div className="rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-[11px] uppercase tracking-wide text-amber-600">Client</p>
                      <p className="text-lg font-bold text-amber-700 dark:text-amber-300 tabular-nums" data-testid="capex-client-value">{fmt(client)} <span className="text-xs font-medium text-slate-400">· {pct(client)}%</span></p>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            {/* Direct Labor / Personnel Section */}
            <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {language === 'fr' ? 'Personnel (Direct Labor)' : 'Staff (Direct Labor)'}
                </h3>
                {applyNewCapexRules && (() => {
                  const totalEtp = (Number(formData.labor_costs.chef_confirme_count) || 0)
                    + (Number(formData.labor_costs.chef_junior_count) || 0)
                    + (Number(formData.labor_costs.second_count) || 0)
                    + (Number(formData.labor_costs.commis_count) || 0);
                  return (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-3 py-1 text-sm font-semibold text-emerald-700 dark:text-emerald-300"
                      data-testid="etp-total-badge"
                    >
                      {language === 'fr' ? `Total : ${totalEtp} ETP` : `Total: ${totalEtp} FTE`}
                    </span>
                  );
                })()}
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {applyNewCapexRules
                  ? (language === 'fr'
                      ? `Calculé automatiquement : 1 % du CA alimentaire ÷ ${caPerEmployee.toLocaleString('fr-FR')} € (1 ETP par salarié), arrondi au plus proche. Répartition : Manager Multisite 0,1 · Chef Confirmé 0 · Chef 1 · Second 1 · Commis = le reste. Modifiable uniquement par un administrateur.`
                      : `Auto-computed: 1% of food revenue ÷ €${caPerEmployee.toLocaleString('en-US')} (1 FTE per employee), rounded. Split: Multisite Manager 0.1 · Senior Chef 0 · Chef 1 · Second 1 · Commis = the rest. Editable by admin only.`)
                  : (language === 'fr'
                      ? "Renseignez le nombre d'ETP (équivalent temps plein) pour chaque poste"
                      : 'Enter the FTE (full-time equivalent) for each position')}
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {/* Manager Multisite */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                  <Label className="text-sm font-medium mb-2 block">Manager Multisite</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    value={formData.labor_costs.manager_multisite_count === 0 ? '' : (formData.labor_costs.manager_multisite_count ?? '')}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      updateFormData('labor_costs', 'manager_multisite_count', val === '' ? 0 : parseFloat(val) || 0);
                    }}
                    className="h-12 text-center text-lg font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={applyNewCapexRules && !isAdmin}
                    data-testid="input-manager-etp"
                  />
                  <span className="text-xs text-slate-400 mt-1 block">ETP</span>
                </div>
                
                {/* Chef Confirmé */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                  <Label className="text-sm font-medium mb-2 block">{language === 'fr' ? 'Chef Confirmé' : 'Senior Chef'}</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    value={formData.labor_costs.chef_confirme_count === 0 ? '' : (formData.labor_costs.chef_confirme_count ?? '')}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      updateFormData('labor_costs', 'chef_confirme_count', val === '' ? 0 : parseFloat(val) || 0);
                    }}
                    className="h-12 text-center text-lg font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={applyNewCapexRules && !isAdmin}
                    data-testid="input-chef-confirme-etp"
                  />
                  <span className="text-xs text-slate-400 mt-1 block">ETP</span>
                </div>
                
                {/* Chef Junior */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                  <Label className="text-sm font-medium mb-2 block">{language === 'fr' ? 'Chef Junior' : 'Junior Chef'}</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    value={formData.labor_costs.chef_junior_count === 0 ? '' : (formData.labor_costs.chef_junior_count ?? '')}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      updateFormData('labor_costs', 'chef_junior_count', val === '' ? 0 : parseFloat(val) || 0);
                    }}
                    className="h-12 text-center text-lg font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={applyNewCapexRules && !isAdmin}
                    data-testid="input-chef-junior-etp"
                  />
                  <span className="text-xs text-slate-400 mt-1 block">ETP</span>
                </div>
                
                {/* Second */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                  <Label className="text-sm font-medium mb-2 block">Second</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    value={formData.labor_costs.second_count === 0 ? '' : (formData.labor_costs.second_count ?? '')}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      updateFormData('labor_costs', 'second_count', val === '' ? 0 : parseFloat(val) || 0);
                    }}
                    className="h-12 text-center text-lg font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={applyNewCapexRules && !isAdmin}
                    data-testid="input-second-etp"
                  />
                  <span className="text-xs text-slate-400 mt-1 block">ETP</span>
                </div>
                
                {/* Commis */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                  <Label className="text-sm font-medium mb-2 block">Commis</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    value={formData.labor_costs.commis_count === 0 ? '' : (formData.labor_costs.commis_count ?? '')}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      updateFormData('labor_costs', 'commis_count', val === '' ? 0 : parseFloat(val) || 0);
                    }}
                    className="h-12 text-center text-lg font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={applyNewCapexRules && !isAdmin}
                    data-testid="input-commis-etp"
                  />
                  <span className="text-xs text-slate-400 mt-1 block">ETP</span>
                </div>
              </div>
            </div>
            
            {/* Durée du contrat - Contract Duration */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {language === 'fr' ? 'Durée de l\'engagement' : 'Contract Duration'}
              </h4>
              <FormSelect
                label={t('contractDuration')}
                id="contract_duration_months"
                value={formData.contract_info.contract_duration_months?.toString() || '36'}
                onChange={(v) => updateFormData('contract_info', 'contract_duration_months', parseInt(v))}
                options={[
                  { value: '12', label: '12 mois' },
                  { value: '24', label: '24 mois' },
                  { value: '36', label: '36 mois' },
                  { value: '48', label: '48 mois' },
                  { value: '60', label: '60 mois' },
                ]}
              />
            </div>
          </div>
        );

      case 6:
        return <TemperatureStep value={formData.deal_temperature} onChange={(f, v) => updateFormData('deal_temperature', f, v)} />;
      case 5:
        return (
          <div className="space-y-6 animate-fade-in">
            {/* Légende code couleur */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-3 text-sm" data-testid="hypotheses-legend">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <span className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                  <span className="inline-block w-1.5 h-5 rounded-sm bg-emerald-400"></span>
                  {language === 'fr' ? 'Modifiable par vous' : 'Editable by you'}
                </span>
                <span className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                  <span className="inline-block w-1.5 h-5 rounded-sm bg-orange-400"></span>
                  {language === 'fr' ? 'Figé — décision de la Direction' : 'Locked — Management decision'}
                </span>
              </div>
            </div>
            {!isAdmin && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 p-3 text-sm text-blue-800 dark:text-blue-200" data-testid="hypotheses-readonly-note">
                {language === 'fr'
                  ? "Ces paramètres sont en lecture seule. Seul le Taux de captation Sushi est modifiable ; les autres valeurs sont ajustables par un administrateur."
                  : 'These parameters are read-only. Only the Sushi captation rate can be edited; other values are adjustable by an administrator.'}
              </div>
            )}
            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t('hypothesis1')}</CardTitle>
                <CardDescription>Basée sur la fréquentation (passage caisse)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormSelect
                    label={t('sushiCaptation')}
                    id="sushi_captation_rate"
                    value={formData.hypothesis_params.sushi_captation_rate?.toString() || '0.03'}
                    onChange={(v) => updateFormData('hypothesis_params', 'sushi_captation_rate', parseFloat(v))}
                    options={[
                      { value: '0.025', label: '2,5%' },
                      { value: '0.03', label: '3%' },
                      { value: '0.035', label: '3,5%' },
                      { value: '0.04', label: '4%' },
                    ]}
                    className="border-l-4 border-l-emerald-400 pl-3 rounded-l-md"
                  />
                  <FormInput
                    label={t('sushiBasket')}
                    id="sushi_basket_avg"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    value={formData.hypothesis_params.sushi_basket_avg ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateFormData('hypothesis_params', 'sushi_basket_avg', val === '' ? 0 : parseFloat(val));
                    }}
                    placeholder="13"
                    disabled={!isAdmin}
                    className="border-l-4 border-l-orange-400 pl-3 rounded-l-md"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormSelect
                    label={t('wokCaptation')}
                    id="wok_captation_rate"
                    value={formData.hypothesis_params.wok_captation_rate?.toString() || '0.01'}
                    onChange={(v) => updateFormData('hypothesis_params', 'wok_captation_rate', parseFloat(v))}
                    options={[
                      { value: '0.01', label: '1%' },
                      { value: '0.015', label: '1,5%' },
                      { value: '0.02', label: '2%' },
                    ]}
                    disabled={!isAdmin}
                    className="border-l-4 border-l-orange-400 pl-3 rounded-l-md"
                  />
                  <FormInput
                    label={t('wokBasket')}
                    id="wok_basket_avg"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    value={formData.hypothesis_params.wok_basket_avg ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateFormData('hypothesis_params', 'wok_basket_avg', val === '' ? 0 : parseFloat(val));
                    }}
                    placeholder="7.5"
                    disabled={!isAdmin}
                    className="border-l-4 border-l-orange-400 pl-3 rounded-l-md"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t('hypothesis2')}</CardTitle>
                <CardDescription>Basée sur le CA alimentaire annuel</CardDescription>
              </CardHeader>
              <CardContent>
                <FormSelect
                  label={t('alimentaireCaptation')}
                  id="alimentaire_captation_rate"
                  value={formData.hypothesis_params.alimentaire_captation_rate?.toString() || '0.01'}
                  onChange={(v) => updateFormData('hypothesis_params', 'alimentaire_captation_rate', parseFloat(v))}
                  options={[
                    { value: '0.005', label: '0,5%' },
                    { value: '0.0075', label: '0,75%' },
                    { value: '0.01', label: '1%' },
                    { value: '0.0125', label: '1,25%' },
                    { value: '0.015', label: '1,5%' },
                    { value: '0.0175', label: '1,75%' },
                    { value: '0.02', label: '2%' },
                  ]}
                  disabled={!isAdmin}
                  className="border-l-4 border-l-orange-400 pl-3 rounded-l-md"
                />
              </CardContent>
            </Card>

            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t('hypothesis3')}</CardTitle>
                <CardDescription>Basée sur la poissonnerie + saurisserie</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label>{t('poissonnerieCaptation')}</Label>
                  <div className="h-12 px-4 rounded-lg border border-slate-200 dark:border-slate-700 border-l-4 border-l-orange-400 bg-slate-100 dark:bg-slate-800 flex items-center text-slate-700 dark:text-slate-300 font-medium">
                    20%
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {language === 'fr' ? 'Valeur fixe non modifiable' : 'Fixed value, not editable'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      /* Photo upload step removed - photos can be added after simulation creation on the result page */

      default:
        return null;
    }
  };

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto" data-testid="new-simulation-page">
        {/* Loading state for edit mode */}
        {loadingSimulation ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h1 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
              {isEditMode 
                ? (language === 'fr' ? 'Modifier la simulation' : 'Edit Simulation')
                : t('newSimulation')}
            </h1>
            <div className="flex items-center gap-3 ml-auto">
              {editActions('top')}
              {/* Draft auto-save indicator (NEW mode only) */}
              {!isEditMode && draftSavedAt && (
                <div
                  className="hidden sm:flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 px-2.5 py-1 rounded-full"
                  title={language === 'fr'
                    ? `Brouillon sauvegardé automatiquement le ${new Date(draftSavedAt).toLocaleString('fr-FR')}`
                    : `Draft auto-saved at ${new Date(draftSavedAt).toLocaleString('en-US')}`}
                  data-testid="draft-saved-badge"
                >
                  <Save className="w-3 h-3" />
                  <span className="font-medium">
                    {language === 'fr' ? 'Brouillon enregistré' : 'Draft saved'}
                  </span>
                </div>
              )}
              {!isEditMode && draftSavedAt && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearDraft}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 gap-1.5"
                  data-testid="clear-draft-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    {language === 'fr' ? 'Effacer brouillon' : 'Clear draft'}
                  </span>
                </Button>
              )}
              <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                {t('step')} {safeCurrentStep} {t('of')} {activeSteps.length}
              </span>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
          
          {/* Mobile compact stepper - dots clickable in edit mode */}
          <div className="md:hidden flex items-center justify-between mt-3 px-1">
            {activeSteps.map((step) => {
              const isActive = safeCurrentStep === step.id;
              const isCompleted = safeCurrentStep > step.id;
              return (
                <button
                  key={step.id}
                  onClick={() => isEditMode && setCurrentStep(step.id)}
                  disabled={!isEditMode}
                  className={`flex flex-col items-center gap-1 flex-1 ${isEditMode ? 'cursor-pointer' : 'cursor-default'}`}
                  data-testid={`mobile-step-${step.id}`}
                  aria-label={`Step ${step.id}: ${t(step.key)}`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-all ${
                    isActive
                      ? 'bg-blue-600 text-white scale-110 shadow-md'
                      : isCompleted
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                  }`}>
                    {isCompleted ? <Check className="w-3 h-3" /> : step.id}
                  </div>
                  <span className={`text-[10px] leading-tight text-center truncate w-full ${
                    isActive ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-slate-400 dark:text-slate-500'
                  }`}>
                    {t(step.key).split(' ')[0]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Step Icons - Clickable in edit mode (desktop only) */}
          <div className="hidden md:flex items-center justify-between mt-4">
            {activeSteps.map((step) => {
              const Icon = step.icon;
              const isActive = safeCurrentStep === step.id;
              const isCompleted = safeCurrentStep > step.id;
              
              return (
                <button
                  key={step.id}
                  onClick={() => isEditMode && setCurrentStep(step.id)}
                  disabled={!isEditMode}
                  className={`flex items-center gap-2 ${isEditMode ? 'cursor-pointer hover:opacity-80' : 'cursor-default'} ${
                    isActive
                      ? 'text-blue-600 dark:text-blue-400'
                      : isCompleted
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-slate-400'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isActive
                      ? 'bg-blue-100 dark:bg-blue-900/50'
                      : isCompleted
                      ? 'bg-emerald-100 dark:bg-emerald-900/50'
                      : 'bg-slate-100 dark:bg-slate-800'
                  }`}>
                    {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form Card */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              {currentStepData && React.createElement(currentStepData.icon, { className: 'w-6 h-6 text-blue-600' })}
              {currentStepData && t(currentStepData.key)}
              {safeCurrentStep === 1 && <div className="ml-auto flex items-center gap-2" data-testid="format-selector">
                <span className="text-xs font-medium text-slate-500">Format</span>
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
                  {[['corner', 'Corner'], ['vitrine_deportee', 'Vitrine déportée']].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`format-${v}`}
                      onClick={() => switchFormat(v)}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${(formData.general_info.format || 'corner') === v ? 'bg-orange-500 text-white' : 'text-slate-600 hover:bg-white'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>}
              {safeCurrentStep > 1 && <span className="ml-auto"><FormatPill format={formData.general_info.format} testid="step-format-pill" /></span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation - sticky on mobile */}
        <div className="sticky bottom-16 lg:bottom-0 lg:relative bg-white/95 dark:bg-slate-900/95 backdrop-blur-md lg:bg-transparent lg:dark:bg-transparent flex items-center justify-between gap-2 mt-6 -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:px-0 py-3 lg:py-0 border-t border-slate-200 dark:border-slate-800 lg:border-0 z-40">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={safeCurrentStep === 1}
            className="gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('back')}
          </Button>
          {editActions('bottom')}
          
          {safeCurrentStep < activeSteps.length ? (() => {
            const missingFields = getMissingRequiredFields(safeCurrentStep);
            const hasMissing = missingFields.length > 0;
            const button = (
              <Button
                onClick={handleNext}
                className={`gap-2 ${hasMissing ? 'opacity-60 cursor-help' : ''}`}
                data-testid="next-btn"
                aria-disabled={hasMissing}
              >
                {t('next')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            );
            if (!hasMissing) return button;
            return (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>{button}</span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="font-semibold mb-1">
                      {language === 'fr' ? 'Champs obligatoires manquants :' : 'Required fields missing:'}
                    </p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {missingFields.map((f) => <li key={f.id}>{f.label}</li>)}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })() : (
            <Button 
              onClick={handleSubmit} 
              disabled={loading} 
              className="gap-2"
              data-testid="calculate-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Calculator className="w-4 h-4" />
                  {isEditMode 
                    ? (language === 'fr' ? 'Mettre à jour' : 'Update')
                    : t('calculate')}
                </>
              )}
            </Button>
          )}
        </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
